import ufeApi from 'services/api/ufeApi';
import { gqlClient } from 'services/api/gql/gqlClient';
import { UpdateAltPickupPerson } from 'constants/gql/mutations/checkout/updateAltPickupPerson.gql';
import OrderUtils from 'utils/Order';

// https://confluence.sephora.com/wiki/pages/viewpage.action?spaceKey=ILLUMINATE&title=Add+Alternate+Pickup+Person+Details+API
// https://confluence.sephora.com/wiki/display/ILLUMINATE/Update+Alternate+Pickup+Person+Details+API

const OXS_URL = '/api/checkout/orders/altPickupPerson';

/** @returns {boolean} Whether OIS modification migration is enabled */
const isOisEnabled = () =>
    Sephora?.configurationSettings?.oisMigration?.global?.isEnabled && Sephora?.configurationSettings?.oisMigration?.modification?.isEnabled;

/** @returns {boolean} Whether the request should be routed to OIS GraphQL */
const shouldUseOis = requestOrigin =>
    isOisEnabled() && requestOrigin === OrderUtils.ORDER_DETAILS_REQUESTS_ORIGIN.ORD_DETAILS_PAGE;

/**
 * Handles alternate pickup person add/update via OIS GraphQL (UpdateAltPickupPerson).
 * Normalizes the GraphQL response to match the legacy OXS shape { firstName, lastName, email }.
 *
 * @param {Object} params - Pickup person data { firstName, lastName, email, orderId }
 * @param {string} requestOrigin - Request origin header value
 * @returns {Promise<Object>} Normalized alt pickup person data { firstName, lastName, email }
 * @throws {Error} Throws with errorCode=-1 and errorMessages for non-SUCCESS responses
 */
const handleOisRequest = async (params, requestOrigin) => {
    const headers = requestOrigin ? { 'x-request-origin': requestOrigin } : undefined;

    const data = await gqlClient.query({
        operationName: 'UpdateAltPickupPerson',
        query: UpdateAltPickupPerson,
        variables: { input: params },
        headers
    });

    const result = data?.updateAltPickupPerson;

    if (result?.responseCode && result.responseCode !== 'SUCCESS') {
        const error = new Error(result.message || 'Alternate pickup person request failed');
        error.errorCode = -1;
        error.responseCode = result.responseCode;
        error.errorMessages = result.message ? [result.message] : [];

        throw error;
    }

    // Normalize GraphQL response to match legacy OXS shape
    const altPickupPerson = result?.order?.altPickupPerson;

    return (
        altPickupPerson || {
            firstName: params?.firstName,
            lastName: params?.lastName,
            email: params?.email
        }
    );
};

/**
 * Handles alternate pickup person add/update via legacy OXS REST API.
 *
 * @param {Object} params - Pickup person data { firstName, lastName, email, orderId }
 * @param {string} requestOrigin - Request origin query param value
 * @param {string} method - HTTP method ('POST' for add, 'PUT' for update)
 * @returns {Promise<Object>} The OXS response data
 * @throws {Object} Throws raw error object when errorCode is present
 */
const handleOxsRequest = async (params, requestOrigin, method) => {
    const requestOriginParamString = requestOrigin ? '?requestOrigin=' + requestOrigin : '';
    const url = OXS_URL + requestOriginParamString;

    const data = await ufeApi.makeRequest(url, {
        method,
        body: JSON.stringify(params)
    });

    if (data?.errorCode) {
        throw data;
    }

    return data;
};

/**
 * Adds an alternate pickup person for an order.
 * Routes to OIS GraphQL when flags are enabled AND origin is ORD_DETAILS;
 * otherwise falls back to legacy OXS REST (e.g. basket/checkout page).
 *
 * @param {Object} params - Pickup person data { firstName, lastName, email, orderId }
 * @param {string} [requestOrigin=''] - Request origin identifier
 * @returns {Promise<Object>} Alt pickup person data { firstName, lastName, email }
 */
export const addAlternatePickupPerson = (params = {}, requestOrigin = '') =>
    shouldUseOis(requestOrigin) ? handleOisRequest(params, requestOrigin) : handleOxsRequest(params, requestOrigin, 'POST');

/**
 * Updates an existing alternate pickup person for an order.
 * Routes to OIS GraphQL when flags are enabled AND origin is ORD_DETAILS;
 * otherwise falls back to legacy OXS REST (e.g. basket/checkout page).
 *
 * @param {Object} params - Pickup person data { firstName, lastName, email, orderId }
 * @param {string} [requestOrigin=''] - Request origin identifier
 * @returns {Promise<Object>} Alt pickup person data { firstName, lastName, email }
 */
export const updateAlternatePickupPerson = (params = {}, requestOrigin = '') =>
    shouldUseOis(requestOrigin) ? handleOisRequest(params, requestOrigin) : handleOxsRequest(params, requestOrigin, 'PUT');
